import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import '@/assets/styles/views/coach/student-details.scss';

import StudentBg from '@/assets/images/student-detail-bg.png';

import { type Student, Tab } from './student-details.types';
import { StudentPerformance, StudentMeetings } from '@/components';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { AvailableHourResponseLiveServiceGroupEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import StudentPackageInfoModal from '@/components/student-package-info-modal';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { formatStudentDates } from '@/utility/formatters/studentInfoFormatter.ts';
import { useApi } from '@/api/useApi.tsx';
import { If } from 'vite-plugin-react-control-statements';
import { sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { jsonTryParse } from '@/utility';

const tabs: Tab[] = [
	{ id: 'meetings', name: 'Görüşmeler', enabled: true },
	{ id: 'performance', name: 'Performans Raporu', enabled: true }
];

const StudentDashboardScreen: React.FC = () => {
	const [activeTab, setActiveTab] = useState<string>('performance');
	const { value: enterTestResultStr } = useFeatureFlag(CONFIGCAT_KEYS.ENTER_TEST_RESULT, '{}');
	const { state } = useLocation();
	const navigate = useNavigate();

	const { StudentStatisticControllerApi } = useApi();

	const [isOpenPackageInfoModal, setIsOpenPackageInfoModal] = useState(false);

	const { data: studentResponse } = useQueryProvider(
		[QUERY_KEYS.findStudentDetailedStatisticsOfLoggedInExpert, state?.studentId],
		() => {
			return StudentStatisticControllerApi.findStudentDetailedStatisticsOfLoggedInExpert(
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				state?.studentId,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined
			);
		},
		{
			enabled: !!state?.studentId
		}
	);

	const student: Student = useMemo(() => {
		if (!studentResponse?.data?.data?.content) {
			return {
				name: '',
				grade: '',
				avatar: undefined
			};
		}

		const stat = studentResponse.data.data.content[0];
		const { formattedDate, isMoreThan21Days } = formatStudentDates(stat?.lastInteractionDate);
		const { formattedDate: formattedMatchingDate } = formatStudentDates(stat?.matchingDate);

		return {
			id: stat?.studentId?.toString() as string,
			shortName: `${stat?.studentName?.charAt(0)?.toUpperCase() || ''} ${stat?.studentSurname?.charAt(0)?.toUpperCase() || ''}`,
			name: `${stat?.studentName || ''} ${stat?.studentSurname || ''}`,
			grade: stat?.preferredGradeName || '-',
			gradeId: stat?.preferredGradeId || undefined,
			avatar: stat?.avatarUrl || undefined,
			meetingCount: stat?.totalInteractionCount || 0,
			lastSolved: stat?.cancelledAppointmentCount || '-',
			lastMeeting: formattedDate,
			isHighlighted: isMoreThan21Days,
			matchingDate: formattedMatchingDate,
			isTopRanked: stat?.isTopRanked
		};
	}, [studentResponse]);

	const handleTabClick = (tabId: string) => {
		setActiveTab(tabId);
	};

	const handleMessageClick = () => {
		navigate(NavigationPath.messages, {
			state: { studentId: student?.id }
		});
	};

	const handleOpenExamForm = () => {
		const url = enterTestResultConfig?.formLink;
		if (!url) return;
		const sent = sendMessageToWebView({
			type: PostMessage.openExternalLink,
			data: { link: url }
		});
		if (!sent) {
			window.open(url, '_blank');
		}
	};

	const enterTestResultConfig = useMemo(() => {
		return jsonTryParse(enterTestResultStr) as { isActive: boolean; formLink: string } | null;
	}, [enterTestResultStr]);

	useEffect(() => {
		if (state && state?.openMeetingsTab) {
			setActiveTab('meetings');
		}
	}, [state]);

	useEffect(() => {
		setTimeout(() => {
			const container = document.querySelector('.main-content-container');
			if (container) {
				container.scrollTop = 0;
			}
		}, 100);
	}, []);

	return (
		<div className="student-dashboard">
			{/* Top Profile Section */}
			<div className="student-dashboard__profile">
				<img
					src={StudentBg}
					className="student-dashboard__profile-bg"
				/>
				<div className="student-dashboard__profile-top">
					<button
						className="back-button"
						onClick={() => {
							window.history.back();
						}}>
						<i className="icons-arrow-left" />
					</button>
				</div>
				<div className="student-dashboard__profile-bottom">
					{/* Profile Image and Info Section */}
					<div className="student-dashboard__profile-top-section">
						{student?.avatar ? (
							<div className="student-dashboard__profile-image">
								<img
									src={student?.avatar}
									alt={student?.name}
								/>
							</div>
						) : (
							<div className="student-dashboard__profile-image student-dashboard__profile-image--placeholder">{student?.shortName}</div>
						)}

						<div className="student-dashboard__profile-info">
							<div className="student-dashboard__profile-name-wrapper">
								<div className="student-dashboard__profile-name">{student?.name}</div>
								<If condition={!!student?.isTopRanked}>
									<div className="student-dashboard__profile-top-ranked">
										<i className="icons-medal" />
										Derece Öğrencisi
									</div>
								</If>
							</div>
							<div className="student-dashboard__profile-grade">{student?.grade}</div>
							<div className="student-dashboard__profile-package-action">
								<button
									className="btn-open-package-info"
									onClick={() => setIsOpenPackageInfoModal(true)}>
									<i className="icons-cap-full" />
									Paket Bilgilerini Gör
								</button>
							</div>
						</div>
					</div>

					{/* Stats and Actions Section */}
					<div className="student-dashboard__profile-bottom-section">
						<div className="student-dashboard__profile-stats">
							<div className="student-dashboard__profile-stats-item">
								<div className="student-dashboard__profile-stats-item-label">Görüşme Sayısı</div>
								<div className="student-dashboard__profile-stats-item-value">{student?.meetingCount || ''}</div>
							</div>
							<div className="student-dashboard__profile-stats-divider" />
							<div className="student-dashboard__profile-stats-item">
								<div className="student-dashboard__profile-stats-item-label">Son Görüşme</div>
								<div className="student-dashboard__profile-stats-item-value">{student?.lastMeeting || ''}</div>
							</div>
						</div>

						<div className="student-dashboard__profile-actions">
							<button
								className="btn btn-primary"
								onClick={handleMessageClick}>
								<i className="icons-letter" />
								Mesaj
							</button>
							<If condition={!!(enterTestResultConfig?.isActive && enterTestResultConfig?.formLink)}>
								<div className="btn-tooltip-wrapper">
									<button
										className="btn btn-secondary btn-icon"
										onClick={handleOpenExamForm}>
										<i className="icons-clipboard-check" />
									</button>
									<span className="btn-tooltip">Deneme Sınavı Gir</span>
								</div>
							</If>
						</div>
					</div>
					<div className="student-dashboard__profile-bottom-package-action">
						<button
							className="btn-open-package-info"
							onClick={() => setIsOpenPackageInfoModal(true)}>
							<i className="icons-cap-full" />
							Paket Bilgilerini Gör
						</button>
					</div>
				</div>
			</div>

			{/* Tabs */}
			<div className="student-dashboard__tabs">
				{tabs.map(tab => (
					<div
						key={tab.id}
						className={clsx('student-dashboard__tabs-item', { 'student-dashboard__tabs-item--active': activeTab === tab.id }, { disabled: !tab?.enabled || false })}
						onClick={() => (tab?.enabled ? handleTabClick(tab.id) : {})}>
						{tab.name}
					</div>
				))}
			</div>

			{/* Main Content Area */}
			{activeTab === 'performance' && (
				<StudentPerformance
					studentId={student?.id?.toString() || ''}
					gradeId={Number(student?.gradeId)}
				/>
			)}
			{activeTab === 'meetings' && (
				<StudentMeetings
					studentId={student?.id?.toString() || ''}
					liveServiceGroup={AvailableHourResponseLiveServiceGroupEnum.GotIt}
				/>
			)}
			<StudentPackageInfoModal
				isOpen={isOpenPackageInfoModal}
				onClose={() => setIsOpenPackageInfoModal(false)}
				studentId={student?.id || ''}
			/>
		</div>
	);
};

export default StudentDashboardScreen;
